import { AuthVerify, Loader } from "@components/common";
import { ENVIRONMENT } from "@constants";
import { StrictMode, Suspense, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Provider } from "react-redux";
import { useSettingsDispatchers } from "@store/dispatchers";
import { registerLogoutCallback } from "@utils/autoLogoutHandler";
import { registerAppEventHandler } from "@utils/appEventHandler";
import useHandleApiEvent from "./hooks/useHandleApiEvent";
import useHandleAutoLogout from "./hooks/useHandleAutoLogout";
import ScrollToTop from "./ScrollToTop";
import Layout from "./layout/Layout";
import store from "./store/store";
import { useSettings } from "./hooks/useSettings";
import "./i18n";
import "./styles/index.css";
import { useAuthState } from "./store/hooks";

function App() {
  return (
    <Suspense fallback={<Loader />}>
      <StrictMode>
        <Provider store={store}>
          <Main />
        </Provider>
      </StrictMode>
    </Suspense>
  );
}

const Main = () => {
  const { settings } = useSettings(["online_offer_payment_enabled"]);
  const { handleSettings } = useSettingsDispatchers();
  const { handleEvent } = useHandleApiEvent();
  const { onAutoLogout } = useHandleAutoLogout();
  const { isLoggedIn } = useAuthState((state) => state);

  useEffect(() => {
    registerLogoutCallback(onAutoLogout);
    registerAppEventHandler(handleEvent);
  }, [handleEvent]);

  useEffect(() => {
    if (isLoggedIn) {
      handleSettings(settings);
    }
  }, [settings]);

  return (
    <>
      {ENVIRONMENT === "production" && (
        <Helmet>
          <script
            type="text/javascript"
            src="https://cdn.cookielaw.org/consent/23c1d24b-0f5a-42fc-8dca-a671ecc4870e/OtAutoBlock.js"
          />
          <script
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            type="text/javascript"
            data-domain-script="23c1d24b-0f5a-42fc-8dca-a671ecc4870e"
          />
          {/* eslint-disable-next-line react/no-danger */}
          <script
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html: "function OptanonWrapper() {}",
            }}
          />
          {/* eslint-enable-next-line react/no-danger */}
        </Helmet>
      )}
      <ScrollToTop />
      <Layout />
      <AuthVerify logOut={() => handleEvent({ type: "LOGOUT" })} />
    </>
  );
};

export default App;
